import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  type TranslationKeys,
  useTranslation,
} from "@/hooks/useTranslation/useTranslation"
import { ArrowRight, ArrowUpRight } from "lucide-react"
import { useMemo } from "react"
import { useNavigate } from "react-router"
import {
  type SupplierDeclarationListItem,
  useDeclarationList,
} from "../../api/declarationsApi"
import {
  type InquiryStatus,
  inquiryStatusLabel,
  type SupplierInquiryListItem,
  useInquiryList,
} from "../../api/supplierInquiriesApi"
import type { LifecycleStatus } from "../../api/types"
import { intlLocale } from "../../utils/date"
import {
  regulationKeyShortLabels,
  regulationLabels,
} from "../../utils/regulationLabel"
import { McPill } from "./McPill"

// Declarations still in flight or awaiting a human decision — "received or
// ongoing". `failed` has no review action to offer here.
const ACTIVE_LIFECYCLE_STATUSES: ReadonlySet<LifecycleStatus> = new Set([
  "pending",
  "processing",
  "ready",
])

// SETTLED IS NOT GONE. These are the reviewed declarations, and this section used
// to drop them on the floor: approving one removed it from the only list the
// supplier has, and nothing else on the record shows it, so a finished declaration
// became unreachable from its own supplier. They are shown as a RECORD — no status
// pill, no call to action, nothing to do — beneath the work that is still open.
const SETTLED_LIFECYCLE_STATUSES: ReadonlySet<LifecycleStatus> = new Set([
  "accepted",
  "expiring",
  "expired",
])

type Translate = (
  key: TranslationKeys,
  dynamicValues?: Record<string, string | number>,
) => string

function declarationStatusPill(
  status: LifecycleStatus,
  translate: Translate,
): {
  label: string
  tone: "muted" | "info" | "attention" | "success" | "danger"
} {
  switch (status) {
    case "pending":
      return { label: translate("MC_MD_WORK_STATUS_RECEIVED"), tone: "muted" }
    case "processing":
      return {
        label: translate("MC_MD_WORK_STATUS_PROCESSING"),
        tone: "info",
      }
    case "ready":
      return {
        label: translate("MC_MD_WORK_STATUS_NEEDS_REVIEW"),
        tone: "attention",
      }
    // accepted/expiring/expired/failed are excluded from the "active"
    // declarations list this pill renders (see ACTIVE_LIFECYCLE_STATUSES
    // below — settled ones get their own no-pill row instead), but the
    // switch stays exhaustive over LifecycleStatus rather than leaking the
    // raw value.
    case "accepted":
      return { label: translate("MC_DECL_LC_ACCEPTED"), tone: "success" }
    case "expiring":
      return { label: translate("MC_DECL_LC_EXPIRING"), tone: "attention" }
    case "expired":
      return { label: translate("MC_DECL_LIFECYCLE_EXPIRED"), tone: "danger" }
    case "failed":
      return { label: translate("MC_DECL_LC_FAILED"), tone: "danger" }
    default:
      return { label: translate("MC_MD_WORK_STATUS_OTHER"), tone: "muted" }
  }
}

function inquiryActionLabel(
  status: InquiryStatus,
  translate: Translate,
): string {
  // Supplier replied and it's waiting on us — everything else is just
  // waiting on the supplier, nothing of ours to review yet.
  if (status === "ureview")
    return translate("MC_MD_WORK_ACTION_REVIEW_RESPONSE")
  return translate("MC_MD_WORK_ACTION_OPEN_ENQUIRY")
}

function declarationActionLabel(
  status: LifecycleStatus,
  translate: Translate,
): string {
  // A completed-but-unapproved declaration is the one to review. One still being read
  // now has something to SHOW rather than nothing — the review route opens on the live
  // board — so the card names that instead of the flat "view".
  if (status === "ready")
    return translate("MC_MD_WORK_ACTION_REVIEW_DECLARATION")
  if (status === "processing" || status === "pending")
    return translate("MC_DECL_ACTION_WATCH_RUN")
  return translate("MC_MD_WORK_ACTION_VIEW_DECLARATION")
}

function formatDate(
  value: string | Date | null | undefined,
  locale: string,
): string | null {
  if (!value) return null
  // A bare `YYYY-MM-DD` (e.g. `validUntil`) is a calendar date, not an instant:
  // `new Date("2027-02-20")` is UTC midnight and shows the day before west of UTC.
  const dateOnly =
    typeof value === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null
  const d = dateOnly
    ? new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      )
    : typeof value === "string"
      ? new Date(value)
      : value
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale)
}

function ActionCard({
  title,
  meta,
  tone,
  statusLabel,
  primaryLabel,
  onPrimary,
}: {
  title: string
  meta: string
  tone: "muted" | "info" | "attention" | "danger" | "success"
  statusLabel: string
  primaryLabel: string
  onPrimary: () => void
}) {
  return (
    <div className="rounded-lg border border-grey4 bg-grey2 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-white">
            {title}
          </div>
          <div className="mt-0.5 text-xs text-grey5">{meta}</div>
        </div>
        <McPill tone={tone}>{statusLabel}</McPill>
      </div>
      <Button size="sm" className="mt-3 gap-1.5 rounded-md" onClick={onPrimary}>
        {primaryLabel}
        <ArrowRight size={14} />
      </Button>
    </div>
  )
}

function inquiryCard(
  row: SupplierInquiryListItem,
  navigate: ReturnType<typeof useNavigate>,
  translate: Translate,
  locale: string,
) {
  const dueDate = formatDate(row.dueAt ?? row.createdAt, locale)
  const metaParts = [
    dueDate && translate("MC_MD_WORK_META_DUE", { date: dueDate }),
    // What the documents actually ask; nothing when that is unknown, never the
    // stored `frameworks` labels (they name FMD, which no document asks).
    row.askedRegulations?.length
      ? regulationKeyShortLabels(row.askedRegulations, translate).join(", ")
      : null,
  ].filter(Boolean)

  return (
    <ActionCard
      key={row.id}
      title={row.reference ?? translate("MC_MD_WORK_INQUIRY")}
      meta={metaParts.join(" · ") || "—"}
      tone={row.statusTone}
      statusLabel={inquiryStatusLabel(row.status, translate)}
      primaryLabel={inquiryActionLabel(row.status, translate)}
      onPrimary={() =>
        navigate(`/material-compliance/supplier-studio/inquiry?id=${row.id}`)
      }
    />
  )
}

/** `row.framework` is the server's comma-joined multi-regulation label
 *  (declaration_interface.frameworks_label); `row.frameworkCodes` names each
 *  one, same order — split/translate/rejoin rather than treating the string as
 *  a single opaque label. */
function declaredFrameworkMeta(
  row: SupplierDeclarationListItem,
  translate: Translate,
): string | null {
  if (!row.framework) return null
  const labels = row.framework.split(", ").filter(Boolean)
  if (labels.length === 0) return null
  return regulationLabels(row.frameworkCodes, labels, translate).join(", ")
}

function declarationCard(
  row: SupplierDeclarationListItem,
  navigate: ReturnType<typeof useNavigate>,
  translate: Translate,
  locale: string,
) {
  const pill = declarationStatusPill(row.lifecycleStatus, translate)
  const receivedDate = formatDate(row.receivedAt, locale)
  const metaParts = [
    receivedDate &&
      translate("MC_MD_WORK_META_RECEIVED", { date: receivedDate }),
    declaredFrameworkMeta(row, translate),
  ].filter(Boolean)

  return (
    <ActionCard
      key={row.id}
      title={row.declarationNumber ?? translate("MC_MD_WORK_DECLARATION")}
      meta={metaParts.join(" · ") || "—"}
      tone={pill.tone}
      statusLabel={pill.label}
      primaryLabel={declarationActionLabel(row.lifecycleStatus, translate)}
      onPrimary={() =>
        navigate(
          `/material-compliance/supplier-studio/declarations/${row.id}/review`,
        )
      }
    />
  )
}

function settledRow(
  row: SupplierDeclarationListItem,
  translate: Translate,
  locale: string,
) {
  // Each date says what it is: the row used to show a bare received date that
  // read like the approval date shown elsewhere on the panel.
  const received = formatDate(row.receivedAt, locale)
  const validUntil = formatDate(row.validUntil, locale)
  const meta = [
    received && translate("MC_MD_WORK_RECEIVED_ON", { date: received }),
    validUntil && translate("MC_MD_WORK_VALID_UNTIL", { date: validUntil }),
    declaredFrameworkMeta(row, translate),
  ]
    .filter(Boolean)
    .join(" · ")
  return (
    <div
      key={row.id}
      className="flex items-baseline justify-between gap-3 border-b border-grey4/50 py-1.5 last:border-b-0"
    >
      {/* The NUMBER holds its width and the META gives way, not the other way
          round. `shrink-0` on the meta meant a row could never be narrower than
          its own longest line — and the meta is every framework the declaration
          covers ("EU RoHS — 2011/65/EU incl. (EU) 2015/863, REACH SVHC — …"),
          so it ran straight out of the card. The number is short and is what
          identifies the row, so it is the one worth keeping whole. */}
      <span className="shrink-0 text-xs text-grey6">
        {row.declarationNumber ?? translate("MC_MD_WORK_DECLARATION")}
      </span>
      {/* Wraps instead of truncating: every regulation the declaration covers
          must be readable, not only the ones that fit on one line. */}
      <span className="min-w-0 text-right text-[11px] wrap-anywhere text-grey5">
        {meta || "—"}
      </span>
    </div>
  )
}

interface SupplierActiveWorkSectionProps {
  /** The supplier whose active inquiries/declarations to show — the
   * supplier's own id on the Supplier panel, or an article's (primary)
   * linked supplier id on the Article panel. */
  supplierId: string | null
}

export function SupplierActiveWorkSection({
  supplierId,
}: SupplierActiveWorkSectionProps) {
  const navigate = useNavigate()
  const { translate, currentLanguage } = useTranslation()
  const locale = intlLocale(currentLanguage)

  const { data: inquiryData, isLoading: inquiriesLoading } = useInquiryList({
    supplierId: supplierId ?? undefined,
    bucket: "open",
    limit: 20,
    enabled: !!supplierId,
  })
  // No server-side "active" filter exists for declarations (only an exact
  // processing_status match) — fetch a page and filter client-side by the
  // derived lifecycleStatus instead.
  const { data: declarationData, isLoading: declarationsLoading } =
    useDeclarationList({
      supplierId: supplierId ?? undefined,
      // A held copy of our own inquiry file is not work on this supplier (TUR-1754).
      held: "exclude",
      limit: 50,
      enabled: !!supplierId,
    })

  const inquiries = inquiryData?.pages.flatMap((p) => p.items) ?? []
  const { declarations, settled } = useMemo(() => {
    const all = declarationData?.pages.flatMap((p) => p.items) ?? []
    return {
      declarations: all.filter((d) =>
        ACTIVE_LIFECYCLE_STATUSES.has(d.lifecycleStatus),
      ),
      settled: all.filter((d) =>
        SETTLED_LIFECYCLE_STATUSES.has(d.lifecycleStatus),
      ),
    }
  }, [declarationData])

  const isLoading = inquiriesLoading || declarationsLoading

  // No resolvable supplier (e.g. an article with no linked supplier) —
  // nothing meaningful to scope this section to.
  if (!supplierId) return null

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2 px-4 pt-3 pb-4">
        <Skeleton className="h-[76px] w-full rounded-lg" />
        <Skeleton className="h-[76px] w-full rounded-lg" />
      </div>
    )
  }

  const hasAny = inquiries.length > 0 || declarations.length > 0
  const totalDeclarations = declarations.length + settled.length

  return (
    <div className="flex flex-col gap-3 px-4 pt-3 pb-4">
      {hasAny && (
        // Capped so a supplier with many open items doesn't push Fields off-screen.
        <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
          {inquiries.map((row) =>
            inquiryCard(row, navigate, translate, locale),
          )}
          {declarations.map((row) =>
            declarationCard(row, navigate, translate, locale),
          )}
        </div>
      )}

      {/* WHY THE SUPPLIER STILL SAYS "NEEDS REVIEW". Its status is red while ANY of
          its declarations is unapproved, which is correct and was unexplained: a
          supplier with two declarations stayed red after one was reviewed, and the
          screen gave no way to tell that from nothing having happened at all. */}
      {totalDeclarations > 0 && (
        <div className="text-xs text-grey5">
          {translate("MC_MD_WORK_REVIEWED_PROGRESS", {
            done: settled.length,
            total: totalDeclarations,
          })}
        </div>
      )}

      {settled.length > 0 && (
        <div className="flex flex-col gap-1 rounded-lg border border-grey4 bg-grey2 px-3 py-2">
          <div className="text-[11px] font-medium tracking-wide text-grey5 uppercase">
            {translate("MC_MD_WORK_ON_RECORD")}
          </div>
          {settled.map((row) => settledRow(row, translate, locale))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          variant={hasAny ? "outline" : "default"}
          className="gap-1.5"
          onClick={() =>
            navigate(
              `/material-compliance/supplier-studio/inquiry/new?supplierIds=${supplierId}`,
            )
          }
        >
          {translate("MC_MD_WORK_REQUEST_DATA")}
          <ArrowRight size={14} />
        </Button>
        <button
          type="button"
          onClick={() => navigate("/material-compliance/supplier-studio")}
          className="flex items-center gap-1 text-xs font-medium text-yellow1 hover:underline"
        >
          {translate("MC_MD_WORK_OPEN_STUDIO")}
          <ArrowUpRight size={12} />
        </button>
      </div>
    </div>
  )
}
